import {
    SocialButtonGroup,
    type SocialButtonGroupItem,
} from '@trustoo/components/SocialButtonGroup/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Social button group (v2) showcase ----------
// Composes SocialButton: a vertical stack of full-width labelled buttons
// (style="buttons") or a horizontal row of icon-only buttons (style="icons").
// Generated from Figma's "Social button groups" set by the component-sync
// pipeline.

const GROUP_ITEMS: SocialButtonGroupItem[] = [
    { social: 'google' },
    { social: 'facebook' },
    { social: 'apple' },
    { social: 'x' },
]

const SOCIAL_BUTTON_GROUP_USAGE = `import { SocialButtonGroup } from '@trustoo/components/SocialButtonGroup/v2'

// Vertical stack of full-width labelled buttons
<SocialButtonGroup
    items={[
        { social: 'google', onClick: signInWithGoogle },
        { social: 'apple', onClick: signInWithApple },
    ]}
/>

// Horizontal row of icon-only buttons
<SocialButtonGroup style="icons" items={[{ social: 'google' }, { social: 'x' }]} />

// Theme forwards to every child
<SocialButtonGroup theme="gray" items={[{ social: 'facebook' }, { social: 'apple' }]} />`

export const SocialButtonGroupShowcase: React.FC = () => (
    <Section
        title="Social button group (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Social button groups</span>{' '}
                set (ComponentSet <InlineCode>5:5186</InlineCode>). Composes{' '}
                <InlineCode>SocialButton</InlineCode> via an <InlineCode>items</InlineCode> array:{' '}
                <InlineCode>style=&quot;buttons&quot;</InlineCode> stacks full-width labelled
                buttons, <InlineCode>style=&quot;icons&quot;</InlineCode> rows icon-only buttons.
                Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/SocialButtonGroup/v2</InlineCode> (net-new component
                — there is no v1 barrel).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="grid gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl sm:grid-cols-2">
                <div className="space-y-spacing-md">
                    <div className="flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">Buttons</span>
                        <InlineCode>style=&quot;buttons&quot;</InlineCode>
                    </div>
                    <SocialButtonGroup items={GROUP_ITEMS} />
                </div>
                <div className="space-y-spacing-md">
                    <div className="flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">Icons</span>
                        <InlineCode>style=&quot;icons&quot;</InlineCode>
                    </div>
                    <SocialButtonGroup style="icons" items={GROUP_ITEMS} />
                </div>
                <div className="space-y-spacing-md">
                    <div className="flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">Color theme</span>
                        <InlineCode>theme=&quot;color&quot;</InlineCode>
                    </div>
                    <SocialButtonGroup theme="color" items={GROUP_ITEMS} />
                </div>
                <div className="space-y-spacing-md">
                    <div className="flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">Gray theme</span>
                        <InlineCode>theme=&quot;gray&quot;</InlineCode>
                    </div>
                    <SocialButtonGroup theme="gray" items={GROUP_ITEMS} />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={SOCIAL_BUTTON_GROUP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Data-driven: each <InlineCode>SocialButtonGroupItem</InlineCode> takes a{' '}
                    <InlineCode>social</InlineCode> plus optional <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>onClick</InlineCode> and <InlineCode>disabled</InlineCode>. The
                    group-level <InlineCode>style</InlineCode> (buttons · icons) and{' '}
                    <InlineCode>theme</InlineCode> (brand · color · gray) forward to every child.
                </p>
            </div>
        </div>
    </Section>
)
